import { Table, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import SwitchWidget from '../../ControlPanel/SwitchWidget';

//utils
import { permissions as generalPermissions } from '../../../../utils/constants';
import { isLockedTrue, offersPermission, getModuleState, getGeneralColumnState } from './permissionGroupUtils';

const InfoTip = ({ id, text }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{text}</Tooltip>}>
        <i className="fa fa-info-circle text-muted opacity-50 font-size-12 ms-1" aria-hidden="true" />
    </OverlayTrigger>
);

const ModuleStateBadge = ({ t, state }) => {
    if (state === 'all') return <span className="perm-group-state perm-group-state-full">{t('Full')}</span>;
    if (state === 'partial') return <span className="perm-group-state perm-group-state-partial">{t('Partial')}</span>;
    return <span className="perm-group-state perm-group-state-none">{t('No access')}</span>;
};

const PermissionGroupMatrix = ({
    t,
    role,
    generalModules,
    rosterModules,
    permissionMap,
    readOnly,
    onPermissionChange,
    onModuleToggle,
    onGeneralColumnToggle,
}) => {
    if (!generalModules.length && !rosterModules.length) {
        return (
            <div className="text-center text-dark font-size-12 py-4">
                {t('No permission module is enabled for this company. Enable the roster or schedule settings from the control panel first.')}
            </div>
        );
    }

    // a column no visible module uses is not shown at all - for a PM that is all
    // four, for a member it drops Manage Members, which Tasks never checks
    const visibleGeneralPermissions = generalPermissions.filter(perm =>
        generalModules.some(mod => offersPermission(generalModules, mod.key, perm.value)));

    return (
        <>
            {generalModules.length ? (
                <div className="mb-3">
                    <div className="d-flex flex-wrap align-items-baseline gap-2 border-bottom pb-2 mb-3">
                        <span className="fw-bold font-size-12 text-uppercase text-dark">{t('General')}</span>
                        <span className="font-size-12 text-dark fw-normal">
                            {role === 'member'
                                ? t('Applies to the modules an employee can administer')
                                : t('Applies to the modules a project manager can administer')}
                        </span>
                    </div>
                    <div className="table-responsive">
                        <Table className="mb-0 align-middle" hover>
                            <thead>
                                <tr>
                                    <th className="text-start text-dark">{t('Module')}</th>
                                    {visibleGeneralPermissions.map(perm => {
                                        const state = getGeneralColumnState(permissionMap, generalModules, perm.value);
                                        return (
                                            <th key={perm.value} className="text-center">
                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input mt-0 perm-col-checkbox cursor-pointer"
                                                        title={state === 'all' ? t('Revoke for all modules') : t('Grant for all modules')}
                                                        checked={state === 'all'}
                                                        disabled={readOnly}
                                                        onChange={() => onGeneralColumnToggle(perm.value, state !== 'all')}
                                                    />
                                                    <span className="font-size-12 fw-bold text-nowrap text-dark">
                                                        {t(perm.label)}
                                                        <InfoTip id={`tip-gen-${perm.value}`} text={`${t(perm.description)}.`} />
                                                    </span>
                                                </div>
                                            </th>
                                        );
                                    })}
                                    <th width="18%" className="text-center text-nowrap text-dark">{t('Access')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {generalModules.map(mod => {
                                    const perms = permissionMap[mod.key] || {};
                                    return (
                                        <tr key={mod.key}>
                                            <td className="text-start fw-bold font-size-12 text-dark align-middle">{t(mod.label)}</td>
                                            {visibleGeneralPermissions.map(perm => {
                                                if (!offersPermission(generalModules, mod.key, perm.value)) {
                                                    return (
                                                        <td key={perm.value} className="text-center align-middle">
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip id={`tip-na-${mod.key}-${perm.value}`}>{t('This module does not use this permission')}</Tooltip>}
                                                            >
                                                                <span className="text-muted">&ndash;</span>
                                                            </OverlayTrigger>
                                                        </td>
                                                    );
                                                }
                                                const locked = isLockedTrue(mod.key, perm.value);
                                                const cell = (
                                                    <SwitchWidget
                                                        className="d-flex justify-content-center"
                                                        value={locked ? true : !!perms[perm.value]}
                                                        disabled={readOnly || locked}
                                                        onChange={checked => onPermissionChange(mod.key, perm.value, checked)}
                                                    />
                                                );
                                                return (
                                                    <td key={perm.value} className="text-center align-middle">
                                                        {locked ? (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip id={`tip-locked-${mod.key}-${perm.value}`}>{t('This permission is always enabled for this module')}</Tooltip>}
                                                            >
                                                                <span className="d-inline-block">{cell}</span>
                                                            </OverlayTrigger>
                                                        ) : cell}
                                                    </td>
                                                );
                                            })}
                                            <td className="text-center align-middle">
                                                <ModuleStateBadge t={t} state={getModuleState(permissionMap, mod)} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                </div>
            ) : null}

            {rosterModules.length ? (
                <div>
                    <div className="d-flex flex-wrap align-items-baseline gap-2 border-bottom pb-2 mb-3">
                        <span className="fw-bold font-size-12 text-uppercase text-dark">{t('Roster')} &amp; {t('Schedule')}</span>
                        <span className="font-size-12 text-dark fw-normal">
                            <i className="fa fa-info-circle me-1" aria-hidden="true" />
                            {t('Some roster features require Roster View. It will be enabled automatically when needed.')}
                        </span>
                    </div>
                    <div className="row gx-3">
                        {rosterModules.map(mod => {
                            const perms = permissionMap[mod.key] || {};
                            const state = getModuleState(permissionMap, mod);
                            const colClass = rosterModules.length <= 2 ? 'col-lg-6' : 'col-xxl-4 col-lg-6';
                            return (
                                <div key={mod.key} className={`${colClass} mb-3 d-flex`}>
                                    <div className="card h-100 w-100 shadow-sm">
                                        <div className={`card-header py-2 px-3 d-flex align-items-center justify-content-between gap-2 ${mod.colorClass || ''}`}>
                                            <span className="d-flex align-items-center gap-2 text-truncate">
                                                <span className="fw-bold font-size-12 text-nowrap text-dark">{t(mod.label)}</span>
                                                <ModuleStateBadge t={t} state={state} />
                                            </span>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip id={`tip-mod-${mod.key}`}>{state === 'all' ? t('Revoke all') : t('Grant all')}</Tooltip>}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input mt-0 perm-col-checkbox cursor-pointer flex-shrink-0"
                                                    checked={state === 'all'}
                                                    disabled={readOnly}
                                                    onChange={() => onModuleToggle(mod.key, state !== 'all')}
                                                />
                                            </OverlayTrigger>
                                        </div>
                                        <div className="card-body py-2">
                                            {mod.permissions.map(perm => (
                                                <div
                                                    key={perm.value}
                                                    className="d-flex align-items-center justify-content-between gap-2 py-2 border-bottom"
                                                >
                                                    <span className="font-size-12 text-dark">
                                                        {t(perm.label)}
                                                        <InfoTip id={`tip-${mod.key}-${perm.value}`} text={t(perm.description)} />
                                                    </span>
                                                    <SwitchWidget
                                                        value={!!perms[perm.value]}
                                                        disabled={readOnly}
                                                        onChange={checked => onPermissionChange(mod.key, perm.value, checked)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {role === 'member' ? (
                        <p className="font-size-12 text-dark mb-0">
                            <i className="fa fa-info-circle me-1" aria-hidden="true" />
                            {t('Roster permissions cannot be assigned to a user with No Department.')}
                        </p>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

export default PermissionGroupMatrix;
